import axios from 'axios';
import { notifySetupStateChanged } from './utils/setupStep';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export interface ApartmentCalendar {
  id: number;
  name: string;
  /** Null for API-backed sources, which have no feed URL. */
  url: string | null;
  source: CalendarSource;
  /** How this source delivers reservations. Absent on rows created before the field existed. */
  connectionType?: ConnectionType;
  /** The provider's own property id, set only on API-backed sources. */
  externalPropertyId?: string;
  externalPropertyName?: string;
  /** PMS_API sources only. Also fetch already-checked-out reservations, up to 180 days back. */
  syncPastReservations?: boolean;
  priority?: number;
  lastSyncedAt?: string;
  lastSyncError?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * An apartment uses either one live API source or N iCal feeds, never both — the provider already
 * carries every channel attached to it, so keeping feeds alongside would import each booking twice.
 */
export type ConnectionType = 'ICAL' | 'PMS_API';

export type CalendarSource = 'AIRBNB' | 'BOOKING' | 'HOSTEX' | 'HOSPITABLE' | 'ECHALUPY' | 'OTHER';

export interface AddCalendarRequest {
  name: string;
  url: string;
  source: CalendarSource;
}

export interface CalendarSyncResult {
  imported: number;
  updated: number;
  cancelled: number;
  skippedPast: number;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
  };
};

export const getCalendars = async (apartmentId: number): Promise<ApartmentCalendar[]> => {
  const res = await axios.get(`${API_BASE}/apartments/${apartmentId}/calendars`, {
    headers: getAuthHeaders(),
    withCredentials: true,
  });
  return res.data;
};

export const addCalendar = async (
  apartmentId: number,
  calendar: AddCalendarRequest
): Promise<ApartmentCalendar> => {
  const res = await axios.post(
    `${API_BASE}/apartments/${apartmentId}/calendars`,
    calendar,
    {
      headers: getAuthHeaders(),
      withCredentials: true,
    }
  );
  notifySetupStateChanged();
  return res.data;
};

export const deleteCalendar = async (apartmentId: number, calendarId: number): Promise<void> => {
  await axios.delete(`${API_BASE}/apartments/${apartmentId}/calendars/${calendarId}`, {
    headers: getAuthHeaders(),
    withCredentials: true,
  });
  notifySetupStateChanged();
};

export const syncCalendar = async (apartmentId: number, calendarId: number): Promise<CalendarSyncResult> => {
  const res = await axios.post(
    `${API_BASE}/apartments/${apartmentId}/calendars/${calendarId}/sync`,
    {},
    { headers: getAuthHeaders(), withCredentials: true }
  );
  return res.data;
};

export const moveCalendarUp = async (apartmentId: number, calendarId: number): Promise<ApartmentCalendar[]> => {
  const res = await axios.post(
    `${API_BASE}/apartments/${apartmentId}/calendars/${calendarId}/priority/up`,
    {},
    { headers: getAuthHeaders(), withCredentials: true }
  );
  return res.data;
};

export const moveCalendarDown = async (apartmentId: number, calendarId: number): Promise<ApartmentCalendar[]> => {
  const res = await axios.post(
    `${API_BASE}/apartments/${apartmentId}/calendars/${calendarId}/priority/down`,
    {},
    { headers: getAuthHeaders(), withCredentials: true }
  );
  return res.data;
};
